import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';

const DialogContext = createContext(null);

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within DialogProvider');
  return ctx;
}

const DEFAULTS = {
  confirm: { title: 'Confirm', confirmText: 'OK', cancelText: 'Cancel', danger: false },
  alert: { title: 'Notice', confirmText: 'OK' },
  prompt: { title: 'Input', confirmText: 'OK', cancelText: 'Cancel', defaultValue: '', label: '', multiline: false },
};

export default function DialogProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  const confirm = useCallback((opts = {}) => {
    const o = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ kind: 'confirm', ...DEFAULTS.confirm, ...o });
    });
  }, []);

  const alert = useCallback((opts = {}) => {
    const o = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ kind: 'alert', ...DEFAULTS.alert, ...o });
    });
  }, []);

  const prompt = useCallback((opts = {}) => {
    const o = typeof opts === 'string' ? { message: opts } : opts;
    const merged = { ...DEFAULTS.prompt, ...o };
    setInputValue(merged.defaultValue || '');
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ kind: 'prompt', ...merged });
    });
  }, []);

  const value = useMemo(() => ({ confirm, alert, prompt }), [confirm, alert, prompt]);

  const open = !!state;
  const isPrompt = state?.kind === 'prompt';
  const isConfirm = state?.kind === 'confirm';
  const isAlert = state?.kind === 'alert';

  const handleCancel = () => close(isPrompt ? null : false);
  const handleConfirm = () => {
    if (isPrompt) close(inputValue);
    else close(true);
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !state?.multiline) {
            e.preventDefault();
            handleConfirm();
          }
        }}
      >
        {state?.title && <DialogTitle>{state.title}</DialogTitle>}
        <DialogContent>
          {state?.message && (
            <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>{state.message}</DialogContentText>
          )}
          {isPrompt && (
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              label={state.label}
              value={inputValue}
              multiline={!!state.multiline}
              minRows={state.multiline ? 3 : undefined}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          {!isAlert && (
            <Button onClick={handleCancel} color="inherit">
              {state?.cancelText || 'Cancel'}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={isConfirm && state?.danger ? 'error' : 'primary'}
            autoFocus={!isPrompt}
          >
            {state?.confirmText || 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </DialogContext.Provider>
  );
}
